import React, { useState, useEffect, useContext, Fragment } from 'react';
import Axios from 'axios';
import styled from 'styled-components';
import { BillsbyUrl } from '../../config/properties';
import { SubsContext } from './SubscriptionInfo';

const PaymentHistoryContainer = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 24px;
  margin-top: 32px;
`;

const TitleContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  padding-bottom: 12px;
  border-bottom: 2px solid #e9ecef;
  
  &:hover {
    opacity: 0.8;
  }
`;

const Title = styled.h2`
  color: #2c3e50;
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 0;
  position: relative;
`;

const TitleControls = styled.div`
  display: flex;
  align-items: center;
  margin-left: 16px;
`;

const ToggleIcon = styled.span`
  font-size: 20px;
  color: #007bff;
  transform: ${props => props.isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};
  transition: transform 0.3s ease;
`;

const ToggleHint = styled.span`
  font-size: 14px;
  color: #6c757d;
  margin-right: 8px;
  font-style: italic;
`;

const ContentContainer = styled.div`
  max-height: ${props => props.isExpanded ? '60vh' : '0'};
  overflow-y: ${props => props.isExpanded ? 'auto' : 'hidden'};
  overflow-x: hidden;
  transition: max-height 0.3s ease-in-out;
  margin-top: ${props => props.isExpanded ? '24px' : '0'};
  
  /* Styling for the scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
    
    &:hover {
      background: #555;
    }
  }
`;

const ContentWrapper = styled.div`
  position: relative;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 150px 250px 200px 1fr;
  gap: 32px;
  align-items: center;
  background: #fff;
  padding: 16px;
  font-weight: 600;
  color: #495057;
  border-bottom: 2px solid #e9ecef;
  position: sticky;
  top: 0;
  z-index: 1;
  margin-bottom: 12px;
  font-size: 16px;
`;

const HeaderCell = styled.div`
  &:last-child {
    text-align: left;
  }
  font-size: 16px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InvoiceCard = styled.div`
  display: grid;
  grid-template-columns: 150px 250px 200px 1fr;
  gap: 32px;
  align-items: center;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const InvoiceSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InvoiceNumber = styled.div`
  color: #2c3e50;
  font-weight: 600;
`;

const BillingPeriod = styled.div`
  color: #2c3e50;
`;

const VisaInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #2c3e50;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StatusSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: flex-start;
  padding-left: 8px;
`;

const Amount = styled.span`
  color: #28a745;
  font-weight: 600;
  font-size: 16px;
`;

const StatusBadge = styled.span`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  background-color: ${props => {
    const status = props.status ? props.status.toLowerCase() : '';
    switch (status) {
      case 'paid':
        return '#e8f5e9';
      case 'pending':
        return '#fff3e0';
      case 'failed':
        return '#ffebee';
      default:
        return '#f5f5f5';
    }
  }};
  color: ${props => {
    const status = props.status ? props.status.toLowerCase() : '';
    switch (status) {
      case 'paid':
        return '#2e7d32';
      case 'pending':
        return '#ef6c00';
      case 'failed':
        return '#c62828';
      default:
        return '#757575';
    }
  }};
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #6c757d;
  font-size: 16px;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #dc3545;
  font-size: 16px;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;
`;

const PageButton = styled.button`
  background: ${props => props.disabled ? '#f8f9fa' : '#fff'};
  border: 1px solid ${props => props.disabled ? '#dee2e6' : '#ef4136'};
  color: ${props => props.disabled ? '#6c757d' : '#ef4136'};
  padding: 8px 16px;
  border-radius: 4px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-weight: 500;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #ef4136;
    color: #fff;
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }
`;

const PageInfo = styled.span`
  color: #495057;
  font-weight: 500;
`;

const PaymentHistory = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const { billsbyCustomerId } = useContext(SubsContext);

  useEffect(() => {
    const fetchPayments = async () => {
      if (billsbyCustomerId) {
        try {
          // Extract just the customer ID part (before the first space)
          const response = await Axios.get(`${BillsbyUrl}/getInvoices/${billsbyCustomerId}`);
          if (response.data) {
            setPayments(response.data);
          } else {
            setPayments([]);
          }
        } catch (err) {
          console.error('Error fetching payment history:', err);
          let errorMsg = 'Failed to load payment history. Please try again later.';

          if (err.response) {
            if (err.response.status === 404) {
              errorMsg = 'No payment history found for this customer.';
            } else if (err.response.data && err.response.data.message) {
              errorMsg = err.response.data.message;
            }
          }

          setErrorMessage(errorMsg);
        }
      }
      setLoading(false);
    };

    fetchPayments();
  }, [billsbyCustomerId]);

  const formatDate = dateString => new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const totalPages = Math.ceil(payments.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = payments.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  return (
    <PaymentHistoryContainer>
      <TitleContainer onClick={() => setIsExpanded(!isExpanded)}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Title>Payment History</Title>
          <TitleControls>
            <ToggleHint>{isExpanded ? 'Click to collapse' : 'Click to expand'}</ToggleHint>
            <ToggleIcon isExpanded={isExpanded}>▼</ToggleIcon>
          </TitleControls>
        </div>
      </TitleContainer>

      <ContentContainer isExpanded={isExpanded}>
        {loading ? (
          <LoadingMessage>Loading payment history...</LoadingMessage>
        ) : errorMessage ? (
          <ErrorMessage>{errorMessage}</ErrorMessage>
        ) : payments.length === 0 ? (
          <ErrorMessage>No payment history found.</ErrorMessage>
        ) : (
          <ContentWrapper>
            <TableHeader>
              <HeaderCell>Invoice #</HeaderCell>
              <HeaderCell>Billing Period</HeaderCell>
              <HeaderCell>Payment Method</HeaderCell>
              <HeaderCell>Status</HeaderCell>
            </TableHeader>
            {currentItems.map(payment => (
              <InvoiceCard key={payment.invoiceId}>
                <InvoiceSection>
                  <InvoiceNumber>#{payment.invoiceNumber}</InvoiceNumber>
                </InvoiceSection>

                <BillingPeriod>
                  {payment.invoice.subscriptions && payment.invoice.subscriptions[0] && (
                    `${formatDate(payment.invoice.subscriptions[0].billingPeriodStart)} - ${formatDate(payment.invoice.subscriptions[0].billingPeriodEnd)}`
                  )}
                </BillingPeriod>

                <VisaInfo>
                  {payment.invoice.cardType && (
                    <Fragment>
                      <span role="img" aria-label="credit card">💳</span>
                      <span>
                        *{payment.invoice.cardLast4Digits} exp {payment.invoice.cardExpiryMonth}/{payment.invoice.cardExpiryYear}
                      </span>
                    </Fragment>
                  )}
                </VisaInfo>

                <StatusSection>
                  <StatusBadge status={payment.formattedAmount === '$0' ? 'free' : payment.status}>
                    {payment.formattedAmount === '$0' ? 'Free' : payment.status}
                  </StatusBadge>
                  <span style={{ color: '#6c757d' }}>•</span>
                  <span>{payment.invoiceType}</span>
                  <span style={{ color: '#6c757d' }}>•</span>
                  <Amount>{payment.formattedAmount}</Amount>
                </StatusSection>
              </InvoiceCard>
            ))}
            {totalPages > 1 && (
              <PaginationContainer>
                <PageButton
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </PageButton>
                <PageInfo>
                  Page {currentPage} of {totalPages}
                </PageInfo>
                <PageButton
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </PageButton>
              </PaginationContainer>
            )}
          </ContentWrapper>
        )}
      </ContentContainer>
    </PaymentHistoryContainer>
  );
};

export default PaymentHistory;


// WEBPACK FOOTER //
// ./src/components/Billsby/PaymentHistory.jsx